import { MARKETPLACE_LICENSE_TYPES } from "../../api/marketplace.js";

export const MAX_LICENSE_BYTES = 15 * 1024 * 1024; // 15 MB

export const LICENSE_STORAGE_NOTICE =
  "All documents are stored securely on our service with end-to-end " +
  "cryptographic keys. Only members from our Operations team can access " +
  "these documents for legal compliance due diligence.";

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function groupDocsByType(docs) {
  const grouped = {};
  docs.forEach((d) => {
    (grouped[d.type] = grouped[d.type] || []).push(d);
  });
  return grouped;
}

/**
 * Client-side checks before an upload. Returns an error message, or null if the
 * file is OK to send.
 */
export function validateLicenseFile(file, existingDocs = []) {
  if (file.size > MAX_LICENSE_BYTES) {
    return `"${file.name}" is ${formatBytes(file.size)} — the max is 15 MB.`;
  }
  // Same filename overwrites server-side today; block to avoid silent data loss.
  if (existingDocs.some((d) => d.original_name === file.name)) {
    return `A file named "${file.name}" was already uploaded here. Rename it and try again.`;
  }
  return null;
}

/**
 * The BE sends a Go zero time ("0001-01-01T00:00:00Z") when no request has been
 * made; normalize that (and anything unparseable) to null.
 * @returns {Date|null}
 */
export function parseRequestedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < 1970) return null;
  return date;
}

/**
 * Whether a seller still owes a document for this type, given the saved
 * opt-out answers (license_answers). Required types always count; opt-out types
 * count only when the seller answered that they need it.
 */
export function isLicenseTypeMissing(licenseType, docs = [], licenseAnswers) {
  if (docs.length) return false;
  if (licenseType.required) return true;
  if (licenseType.optOut) return licenseAnswers?.[licenseType.type] === true;
  return false;
}

/**
 * Whether a license type is satisfied for completing the licenses step.
 * naFlags: type -> boolean, only meaningful for optOut categories.
 */
export function isLicenseTypeSatisfied(licenseType, docsByType, naFlags = {}) {
  const hasDocs = (docsByType[licenseType.type] || []).length > 0;
  if (licenseType.required) return hasDocs;
  if (licenseType.optOut) return naFlags[licenseType.type] === true || hasDocs;
  return true; // fully optional (e.g. Other Documents)
}

/**
 * The three opt-out categories drive the status booleans; checking N/A means
 * "false" (doesn't sell / doesn't hold), otherwise true.
 */
export function buildOptOutAnswers(naFlags = {}) {
  return Object.fromEntries(
    MARKETPLACE_LICENSE_TYPES.filter((t) => t.optOut).map((t) => [
      t.type,
      naFlags[t.type] !== true,
    ])
  );
}
